import { useMemo } from 'react';

import type { AiModelContent } from '@/constants/aiLeague/aiLeagueTypes';
import type { AiLeagueChartUserInput } from '@/hooks/useAiLeaguePageData';
import { useAiLeagueScoringContext } from '@/hooks/useAiLeagueScoringContext';
import { buildAiLeagueHistoryColumns } from '@/helpers/leagues/buildAiLeagueHistory';
import { buildStandingsSeries } from '@/helpers/leagues/standingsSeries';
import type { gameResult } from '@/models/gameUpdate';
import type { leagueMember } from '@/models/user';
import type { Dict } from '@/helpers';

function membersByUidFromHistory(
  columns: ReturnType<typeof buildAiLeagueHistoryColumns>,
  aiModels: AiModelContent[],
  chartUser?: AiLeagueChartUserInput,
): Dict<leagueMember> {
  const members: Dict<leagueMember> = {};
  for (const model of aiModels) {
    members[model.id] = { name: model.displayName } as leagueMember;
  }
  if (chartUser) {
    members[chartUser.id] = { name: chartUser.name } as leagueMember;
  }
  for (const col of columns) {
    for (const uid of col.order) {
      if (!members[uid]) {
        members[uid] = { name: uid } as leagueMember;
      }
    }
  }
  return members;
}

export function useAiLeagueStandingsData(props: {
  gameResults: gameResult[];
  settledMatchday: number;
  aiModels: AiModelContent[];
  chartUser?: AiLeagueChartUserInput;
}) {
  const scoring = useAiLeagueScoringContext();

  const columns = useMemo(
    () =>
      buildAiLeagueHistoryColumns({
        gameResults: props.gameResults,
        throughMatchday: props.settledMatchday,
        aiModels: props.aiModels,
        user: props.chartUser,
        progressionAwards: scoring.progressionAwards,
        matchdayCount: scoring.matchdayCount,
        swapMatchday: scoring.swapMatchday,
      }),
    [
      props.gameResults,
      props.settledMatchday,
      props.aiModels,
      props.chartUser,
      scoring.matchdayCount,
      scoring.progressionAwards,
      scoring.swapMatchday,
    ],
  );

  const membersByUid = useMemo(
    () => membersByUidFromHistory(columns, props.aiModels, props.chartUser),
    [columns, props.aiModels, props.chartUser],
  );

  const standings = useMemo(
    () => buildStandingsSeries(columns, membersByUid),
    [columns, membersByUid],
  );

  const ready =
    props.settledMatchday >= 1 && standings.maxMatchday >= 1 && standings.series.length > 0;

  return {
    columns,
    standings,
    ready,
    currentUid: props.chartUser?.id,
    gameResults: props.gameResults,
  };
}
